import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { Box, Button, Divider, IconButton, Stack } from "@mzc-pdc/ui";
import { alpha } from "@mui/material";
import { IconAdd, IconCheck, IconErrorCircle, IconSchema } from "@mzc-cloudplex/icons";
import theme from "@styles/theme";
import Typography from "@components_v2/typography/typography";
import PanelPropertyTitle from "@components/view/components/panel-property-title";
import MetadataFieldReadMode from "@features/metadata-fields/metadata-field-read-mode";
import { getMetadataIcon } from "@features/content-schema-fields/content-schema-type-icons";
import Tooltip from "@components_v2/Tooltip/tooltip";
import { EllipsisTypography } from "@components_v2/ellipsis-tooltip";

const isEmptyValue = (v) => v === null || v === undefined || v === "" || (Array.isArray(v) && v.length === 0);

/**
 * 필드의 값(추출값)을 validationRule로 검증해 첫 에러 메시지를 반환한다(없으면 null).
 * validationRule은 useCustomContent의 createDefaultValueSchema가 타입별로 생성하며,
 * 검증 스키마를 만들 수 없는(null 반환) 타입은 자연히 검증 대상에서 제외된다.
 * @param {object} field getCustomMetadataByContentSchema 산출 필드(value/validationRule 포함)
 * @param {(key:string, fallback:string)=>string} t i18n translate 함수
 * @returns {string|null} 번역된 에러 메시지 또는 null
 */
export const getFieldValidationError = (field, t) => {
    if (!field?.validationRule) return null;
    try {
        field.validationRule.validateSync({ fieldValue: field.value ?? field.defaultValue }, { abortEarly: false });
        return null;
    } catch (e) {
        const message = e?.errors?.[0];
        return message ? t(`common::msg::${message}`, message) : null;
    }
};

/**
 * 커스텀 메타데이터 스키마 카드 (presentational, redux/fetch 없음).
 *
 * 스키마 헤더(아이콘 + 이름 + "필드 N")와 필드 목록(타입 아이콘 + 이름 + 필수 표시 + 하단 정보)을 렌더한다.
 * 프리셋 상세 패널 읽기 카드(definition)와 향후 AI Workspace 추출 결과 카드(result)가 공유한다.
 *
 * @param {object} props
 * @param {string} props.schemaName 스키마 이름
 * @param {Array<{id:string, subtitle:string, type:string, typeName?:string, isRequired:boolean, description?:string, value?:*, defaultValue?:*, options?:Array}>} props.fields
 *   `getCustomMetadataByContentSchema` 산출 필드 배열
 * @param {"definition"|"result"} [props.variant="definition"] 필드행 하단 표기 — definition: 필드 타입 이름, result: 값
 * @param {(field:object) => void} [props.onClickFieldAction] 필드행 우측 액션 버튼 클릭 핸들러.
 * @param {boolean} [props.showFieldAction=false] true(편집 모드)면 필드 액션 버튼을 항상 노출하고, 헤더를 편집 모드 레이아웃(흰 배경 + 스키마명 라인 우측 "전체 적용" 버튼 + "필드 N" 다음 라인)으로 렌더한다.
 * @param {boolean} [props.fieldActionDisabled=false] true면 액션 버튼을 비활성화한다(선행 조건 미충족 등).
 * @param {string} [props.fieldActionDisabledReason] 비활성 사유 문구. fieldActionDisabled일 때 액션 버튼 툴팁을 이 문구로 대체한다.
 *   비우면 기존 "적용"/"적용 해제" 툴팁을 그대로 쓴다.
 * @param {(fields:Array) => void} [props.onClickApplyAll] 편집 모드 헤더 "전체 적용" 버튼 클릭 핸들러. fields 전체를 전달한다.
 * @param {boolean} [props.applyAllDisabled=false] true면 "전체 적용" 버튼을 비활성화한다.
 * @param {string} [props.applyAllDisabledReason] 비활성 사유 문구. applyAllDisabled일 때 "전체 적용" 버튼에 이 문구의 툴팁을 띄운다.
 *   비우면 툴팁을 띄우지 않는다(기존 동작).
 * @param {Array<string>} [props.appliedFieldIds=[]] 이미 적용된 필드 id 목록. 해당 필드의 액션 버튼이 체크 + "적용 해제" 툴팁으로 바뀐다.
 *   전부 적용되면 "전체 적용" 버튼은 렌더하지 않는다.
 * @param {boolean} [props.hideFields=false] true면 헤더(이름 + "필드 N")만 렌더 — 편집 모드 선택 스키마 요약 카드용
 */
const CustomMetadataSchemaCard = ({
    schemaName,
    fields = [],
    variant = "definition",
    onClickFieldAction,
    showFieldAction = false,
    fieldActionDisabled = false,
    fieldActionDisabledReason = "",
    onClickApplyAll,
    applyAllDisabled = false,
    applyAllDisabledReason = "",
    appliedFieldIds = [],
    hideFields = false,
}) => {
    const { t } = useTranslation();

    // showFieldAction(편집 모드)에서 required인데 값이 없는 필드 (추출 안됨 → 적용 대상 제외)
    const isFieldRequiredEmpty = (field) =>
        showFieldAction && field.isRequired && isEmptyValue(field.value ?? field.defaultValue);

    // 필드 값 검증 에러(required-empty가 아니고 result 변형일 때만) — 값 표시/적용 대상 판단에 공용 사용.
    const getFieldError = (field) =>
        variant === "result" && !isFieldRequiredEmpty(field) ? getFieldValidationError(field, t) : null;

    // "전체 적용" 대상: 예외 없이 모든 필드.
    const applicableFields = fields;

    const isAllApplied = applicableFields.length > 0 && applicableFields.every((f) => appliedFieldIds.includes(f.id));

    return (
        <Box
            sx={{
                width: "100%",
                border: `1px solid ${theme.palette.greySecondary[100]}`,
                borderRadius: theme.spacing(0.5),
                overflow: "hidden",
            }}
        >
            {showFieldAction ? (
                <Stack
                    direction={"row"}
                    alignItems={"center"}
                    gap={1}
                    sx={{ px: 1.5, py: 1, backgroundColor: theme.palette.common.white }}
                >
                    {/* 왼쪽 컬럼: 1행 아이콘 + 스키마명 / 2행 필드 개수 */}
                    <Stack direction={"column"} flex={1} gap={0.5} sx={{ minWidth: 0 }}>
                        <Stack direction={"row"} alignItems={"center"} gap={1}>
                            <IconSchema size={16} htmlColor={theme.palette.success.main} />
                            <EllipsisTypography
                                variant={"body1_500"}
                                color={theme.palette.greySecondary.main}
                                sx={{
                                    flex: 1,
                                    minWidth: 0,
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                }}
                            >
                                {schemaName}
                            </EllipsisTypography>
                        </Stack>
                        <Typography variant={"body3_400"} color={theme.palette.greySecondary[600]} sx={{ pl: 3 }}>
                            {t(`common::label::Field {{count}}`, `Field {{count}}`, { count: fields.length })}
                        </Typography>
                    </Stack>
                    {/* 오른쪽 컬럼: 두 행 병합 + 버튼(세로 가운데 정렬) */}
                    {!isAllApplied && (
                        // 비활성 상태의 Button 은 hover 이벤트를 내보내지 않으므로 Box 로 감싸 툴팁을 받는다.
                        <Tooltip title={applyAllDisabled ? applyAllDisabledReason : ""} placement={"bottom"}>
                            <Box sx={{ display: "inline-flex", flexShrink: 0 }}>
                                <Button
                                    variant={"outlined"}
                                    color={"primary"}
                                    size={"xsmall"}
                                    startIcon={<IconAdd size={16} />}
                                    disabled={applyAllDisabled}
                                    onClick={() => onClickApplyAll?.(applicableFields)}
                                    sx={{ flexShrink: 0 }}
                                >
                                    {t(`common::label::Apply all`, `Apply all`)}
                                </Button>
                            </Box>
                        </Tooltip>
                    )}
                </Stack>
            ) : (
                <Stack
                    direction={"row"}
                    alignItems={"center"}
                    gap={1}
                    sx={{ px: 1.5, py: 1, backgroundColor: alpha(theme.palette.greySecondary[50], 0.24) }}
                >
                    <IconSchema size={16} htmlColor={theme.palette.success.main} />
                    <Typography
                        variant={"body1_500"}
                        color={theme.palette.greySecondary.main}
                        sx={{
                            flex: 1,
                            minWidth: 0,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                        }}
                    >
                        {schemaName}
                    </Typography>
                    <Typography
                        variant={"body3_400"}
                        color={theme.palette.greySecondary[600]}
                        sx={{ whiteSpace: "nowrap" }}
                    >
                        {t(`common::label::Field {{count}}`, `Field {{count}}`, { count: fields.length })}
                    </Typography>
                </Stack>
            )}

            {!hideFields &&
                fields.map((field, index) => {
                    // showFieldAction(편집 모드)에서 required인데 값이 없으면 에러 표시
                    const isRequiredEmpty = isFieldRequiredEmpty(field);
                    // 추출값 검증 에러(required-empty가 아닌 필드만) — 값 앞 아이콘 + 툴팁으로 노출
                    const fieldError = getFieldError(field);
                    const isFieldApplied = appliedFieldIds.includes(field.id);
                    // 버튼이 하는 일(=접근성 이름)과 툴팁 문구를 분리한다 — 비활성 사유는 툴팁에만 싣는다.
                    const fieldActionLabel = isFieldApplied
                        ? t(`common::label::Unapply`, `Unapply`)
                        : t(`common::label::Apply`, `Apply`);
                    const fieldActionTooltip =
                        fieldActionDisabled && fieldActionDisabledReason ? fieldActionDisabledReason : fieldActionLabel;
                    return (
                        <Box key={field.id ?? index}>
                            <Divider sx={{ borderColor: theme.palette.greySecondary[100] }} />
                            <Stack
                                direction={"row"}
                                alignItems={"flex-start"}
                                gap={1.5}
                                sx={{
                                    px: 1.5,
                                    py: 1,
                                    // 필수 미입력이거나 검증 에러가 있으면 에러 배경. (선택/적용 상태로는 배경을 바꾸지 않는다)
                                    ...(isRequiredEmpty || fieldError
                                        ? { backgroundColor: alpha(theme.palette.error[50], 0.5) }
                                        : {}),
                                }}
                            >
                                <Box
                                    width={20}
                                    height={20}
                                    sx={{
                                        flexShrink: 0,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        borderRadius: theme.spacing(0.25),
                                        backgroundColor: theme.palette.secondary[800],
                                    }}
                                >
                                    {getMetadataIcon(field.type)}
                                </Box>
                                <Stack direction={"column"} flex={1} sx={{ minWidth: 0 }}>
                                    <Stack direction={"row"} flex={1}>
                                        <PanelPropertyTitle
                                            variant={"body3"}
                                            color={theme.palette.greySecondary[600]}
                                            required={field.isRequired}
                                            subtitle={field.subtitle}
                                            tooltip={field.description}
                                        />
                                    </Stack>
                                    {isRequiredEmpty ? (
                                        <Stack direction={"row"} alignItems={"center"} gap={0.5}>
                                            <IconErrorCircle size={16} htmlColor={theme.palette.error.main} />
                                            <Typography variant={"body3_400"} color={theme.palette.error.main}>
                                                {t(`common::label::Not extracted`, `Not extracted`)}
                                            </Typography>
                                        </Stack>
                                    ) : (
                                        <Stack
                                            direction={"row"}
                                            alignItems={"flex-start"}
                                            gap={0.5}
                                            sx={{ minWidth: 0 }}
                                        >
                                            {fieldError && (
                                                <Tooltip title={fieldError} placement={"bottom"}>
                                                    <Box
                                                        sx={{
                                                            display: "inline-flex",
                                                            marginTop: 0.27,
                                                            flexShrink: 0,
                                                        }}
                                                    >
                                                        <IconErrorCircle
                                                            size={16}
                                                            htmlColor={theme.palette.error.main}
                                                        />
                                                    </Box>
                                                </Tooltip>
                                            )}
                                            <Typography
                                                variant={"body1"}
                                                color={
                                                    fieldError
                                                        ? theme.palette.error.main
                                                        : theme.palette.greySecondary.main
                                                }
                                                sx={{ minWidth: 0 }}
                                            >
                                                {variant === "result" ? (
                                                    <MetadataFieldReadMode
                                                        type={field.type}
                                                        isHidden={false}
                                                        value={field.value ?? field.defaultValue}
                                                        options={field.options}
                                                    />
                                                ) : (
                                                    field.typeName
                                                )}
                                            </Typography>
                                        </Stack>
                                    )}
                                </Stack>
                                {(showFieldAction || onClickFieldAction) && (
                                    <Tooltip title={fieldActionTooltip} placement={"bottom"}>
                                        <Box sx={{ display: "inline-flex", alignSelf: "center" }}>
                                            <IconButton
                                                circled
                                                aria-label={fieldActionLabel}
                                                // 아래 sx 의 색 오버라이드와 조건을 맞춘다.
                                                variant={
                                                    isFieldApplied && !fieldActionDisabled ? "contained" : "outlined"
                                                }
                                                size={"small"}
                                                color={fieldActionDisabled ? "greySecondary" : "primary"}
                                                disabled={fieldActionDisabled}
                                                onClick={() => onClickFieldAction?.(field)}
                                                sx={{
                                                    flexShrink: 0,
                                                    ...(isFieldApplied && !fieldActionDisabled
                                                        ? {
                                                              backgroundColor: theme.palette.primary.main,
                                                              color: theme.palette.common.white,
                                                              "&:hover": {
                                                                  backgroundColor: theme.palette.primary.dark,
                                                              },
                                                          }
                                                        : {}),
                                                }}
                                            >
                                                {isFieldApplied ? <IconCheck size={12} /> : <IconAdd size={12} />}
                                            </IconButton>
                                        </Box>
                                    </Tooltip>
                                )}
                            </Stack>
                        </Box>
                    );
                })}
        </Box>
    );
};

CustomMetadataSchemaCard.propTypes = {
    schemaName: PropTypes.string,
    fields: PropTypes.array,
    variant: PropTypes.oneOf(["definition", "result"]),
    onClickFieldAction: PropTypes.func,
    showFieldAction: PropTypes.bool,
    fieldActionDisabled: PropTypes.bool,
    fieldActionDisabledReason: PropTypes.string,
    onClickApplyAll: PropTypes.func,
    applyAllDisabled: PropTypes.bool,
    applyAllDisabledReason: PropTypes.string,
    appliedFieldIds: PropTypes.arrayOf(PropTypes.string),
    hideFields: PropTypes.bool,
};

export default CustomMetadataSchemaCard;
